import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Eye,
  Loader2,
  Megaphone,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { platformAPI } from '../../services/platformApi';
import { PlanBadge } from './PlanPanel';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  provisioning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  sandbox: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
};

const LEVEL_STYLES = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
};

const errorText = (e) =>
  e?.response?.data?.error || e?.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join(', ') || e?.message;

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

export function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.provisioning}`}>
      {status}
    </span>
  );
}

const input =
  'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white';

/** The browser's own zone is the likeliest right answer for whoever is typing. */
const guessTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/**
 * Every tenant, and the form that creates one.
 *
 * Modelled on ApiKeysPage: a table, a create modal, and a one-time result
 * panel. Provisioning is synchronous on the server — schema, role, grants,
 * the whole migration set and the isolation check — so the modal waits, and
 * a timeout is not a failure: the same submit again resumes the build.
 */
export default function TenantsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slug: '', name: '', timezone: guessTimezone(), plan: 'trial' });
  const [result, setResult] = useState(null);
  const [showRestore, setShowRestore] = useState(false);
  const [restoreForm, setRestoreForm] = useState({ slug: '', name: '', file: null });
  const [restoreResult, setRestoreResult] = useState(null);
  const [banner, setBanner] = useState({ message: '', level: 'info', tenantSlug: '', endsAt: '' });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: () => platformAPI.listTenants(),
    select: (r) => r.data.tenants,
  });

  const provision = useMutation({
    mutationFn: (body) => platformAPI.provision(body),
    onSuccess: (r) => {
      setResult(r.data);
      setShowCreate(false);
      setForm({ slug: '', name: '', timezone: guessTimezone(), plan: 'trial' });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
  });

  const restore = useMutation({
    mutationFn: ({ slug, name, file }) => platformAPI.restore(slug, file, name || undefined),
    onSuccess: (r) => {
      setRestoreResult(r.data);
      setShowRestore(false);
      setRestoreForm({ slug: '', name: '', file: null });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
  });

  const banners = useQuery({
    queryKey: ['platform-banners'],
    queryFn: () => platformAPI.listBanners(),
    select: (r) => r.data.banners,
  });

  const createBanner = useMutation({
    mutationFn: (body) => platformAPI.createBanner(body),
    onSuccess: () => {
      setBanner({ message: '', level: 'info', tenantSlug: '', endsAt: '' });
      queryClient.invalidateQueries({ queryKey: ['platform-banners'] });
    },
  });

  const deleteBanner = useMutation({
    mutationFn: (id) => platformAPI.deleteBanner(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-banners'] }),
  });

  const tenants = data || [];
  const slugOk = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(form.slug);
  const restoreSlugOk = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(restoreForm.slug);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Tenants</h1>
          <p className="mt-1 text-neutral-600 dark:text-neutral-400">One schema and one database role per MSP.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowRestore(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
            title="Rebuild a tenant whose schema is gone from its export file"
          >
            <Upload className="h-4 w-4" />
            Restore from dump
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
          >
            <Plus className="h-5 w-5" />
            New tenant
          </button>
        </div>
      </div>

      {restoreResult && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/30">
          <div className="flex items-start gap-4">
            <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 dark:text-green-400" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 dark:text-green-300">
                Tenant {restoreResult.tenant.slug} restored and {restoreResult.tenant.status}
                {restoreResult.rowReused ? ' (registry row kept)' : ' (new registry row)'}
              </h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                {restoreResult.counts.tables} tables · {restoreResult.counts.migrations} migrations in the ledger ·{' '}
                {restoreResult.counts.users} users · isolation {restoreResult.isolation?.ok ? 'verified' : 'FAILED'}
              </p>
              <pre className="mt-3 max-h-48 overflow-auto rounded bg-white p-3 font-mono text-xs text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                {restoreResult.log.join('\n')}
              </pre>
              <p className="mt-3 text-sm text-green-800 dark:text-green-300">
                Integration credentials live in the registry, not the dump: if the registry row was gone, configure
                ConnectWise again from the tenant's admin area.
              </p>
            </div>
            <button type="button" onClick={() => setRestoreResult(null)} className="text-green-600 hover:text-green-800">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
          <p className="text-red-700 dark:text-red-300">{error.response?.data?.error || 'Failed to load tenants'}</p>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/30">
          <div className="flex items-start gap-4">
            <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 dark:text-green-400" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 dark:text-green-300">
                Tenant {result.tenant.slug} is {result.tenant.status}
                {result.resumed ? ' (resumed)' : ''}
              </h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                {result.migrations.applied} migrations applied, {result.migrations.present} already present · isolation{' '}
                {result.isolation.ok ? 'verified' : 'FAILED'} · timezone {result.timezone}
              </p>
              <pre className="mt-3 max-h-48 overflow-auto rounded bg-white p-3 font-mono text-xs text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                {result.log.join('\n')}
              </pre>
              <p className="mt-3 text-sm text-green-800 dark:text-green-300">
                Next: configure its ConnectWise with{' '}
                <code>node scripts/set-tenant-integration.js {result.tenant.slug} connectwise</code>, then invite its
                first admin from the tenant page.
              </p>
            </div>
            <button type="button" onClick={() => setResult(null)} className="text-green-600 hover:text-green-800">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600" />
            <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-white">No tenants</h3>
            <p className="mt-2 text-neutral-500 dark:text-neutral-400">
              A pre-tenancy install is adopted with <code>provision-tenant.js --adopt-public</code>; everything after
              that can be created here.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-700/50">
              <tr>
                {['Tenant', 'Status', 'ConnectWise', 'Setup', 'Usage (30d)', 'Provisioned', ''].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {tenants.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
                  onClick={() => navigate(`/manage/tenants/${t.slug}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-neutral-900 dark:text-white">{t.name}</div>
                    <div className="text-sm text-neutral-500 dark:text-neutral-400">
                      <code>{t.slug}</code> · {t.schemaName}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap items-center gap-1">
                      <StatusBadge status={t.status} />
                      <PlanBadge plan={t.plan} />
                    </div>
                    {t.plan === 'trial' && t.trialEndsAt && (
                      <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">trial ends {String(t.trialEndsAt).slice(0, 10)}</div>
                    )}
                    {t.deletedHoldUntil && (
                      <div className="mt-1 text-xs text-red-600 dark:text-red-400" title="Scheduled for deletion">
                        drops {fmt(t.deletedHoldUntil).split(',')[0]}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {t.hasConnectWise ? (
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300">
                        <CheckCircle className="h-4 w-4" /> configured
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                        title="Still relies on the environment. Blocks provisioning another tenant."
                      >
                        <XCircle className="h-4 w-4" /> from env
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {t.setup ? (
                      <span
                        className={t.setup.complete === t.setup.total ? 'text-green-700 dark:text-green-300' : 'text-amber-600 dark:text-amber-400'}
                        title={t.setup.steps
                          ?.filter((x) => !x.done)
                          .map((x) => x.label)
                          .join(', ') || 'Complete'}
                      >
                        {t.setup.complete} / {t.setup.total}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                    {t.usage30?.days > 0 ? (
                      <span title={`${t.usage30.days} days recorded, latest ${t.usage30.latestDay}`}>
                        {t.usage30.users} users · {t.usage30.emailsSent} emails · {t.usage30.apiCalls} API · {fmtBytes(t.usage30.storageBytes)}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">{fmt(t.provisionedAt)}</td>
                  <td className="px-6 py-4 text-right">
                    <Eye className="inline h-4 w-4 text-neutral-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Operator banners */}
      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Banners</h2>
        </div>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          A notice shown as a strip in the portal shell, to one tenant or to all of them. Anyone who opens that portal
          can read it.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createBanner.mutate({
              message: banner.message.trim(),
              level: banner.level,
              tenantSlug: banner.tenantSlug || undefined,
              endsAt: banner.endsAt ? new Date(banner.endsAt).toISOString() : undefined,
            });
          }}
          className="mt-4 grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto]"
        >
          <input
            type="text"
            required
            value={banner.message}
            onChange={(e) => setBanner({ ...banner, message: e.target.value })}
            placeholder="Maintenance Sunday 02:00–03:00 UTC: the portal will be unavailable."
            className={input}
          />
          <select value={banner.level} onChange={(e) => setBanner({ ...banner, level: e.target.value })} className={input}>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
          <select value={banner.tenantSlug} onChange={(e) => setBanner({ ...banner, tenantSlug: e.target.value })} className={input}>
            <option value="">All tenants</option>
            {tenants
              .filter((t) => t.status !== 'sandbox')
              .map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.slug}
                </option>
              ))}
          </select>
          <input
            type="datetime-local"
            value={banner.endsAt}
            onChange={(e) => setBanner({ ...banner, endsAt: e.target.value })}
            title="Ends at (optional)"
            className={input}
          />
          <button
            type="submit"
            disabled={createBanner.isPending || !banner.message.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {createBanner.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Post
          </button>
        </form>
        {createBanner.error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errorText(createBanner.error)}</p>}
        {banners.data?.length > 0 ? (
          <ul className="mt-4 divide-y divide-neutral-200 dark:divide-neutral-700">
            {banners.data.map((b) => {
              const live = (!b.startsAt || new Date(b.startsAt) <= new Date()) && (!b.endsAt || new Date(b.endsAt) > new Date());
              return (
                <li key={b.id} className="flex items-start justify-between gap-4 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL_STYLES[b.level] || LEVEL_STYLES.info}`}>{b.level}</span>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        {b.tenantSlug ? <code>{b.tenantSlug}</code> : 'all tenants'} · {live ? 'live' : 'not in window'} ·{' '}
                        {b.endsAt ? `until ${fmt(b.endsAt)}` : 'no end'} · {b.createdBy || '—'}
                      </span>
                    </div>
                    <p className="mt-1 text-neutral-900 dark:text-white">{b.message}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteBanner.mutate(b.id)}
                    disabled={deleteBanner.isPending}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">No banners.</p>
        )}
      </section>

      {showRestore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-neutral-800 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-neutral-200 p-6 dark:border-neutral-700">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Restore a tenant from its dump</h2>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                For a slug whose schema no longer exists. Creates the schema and role with the provisioning grants, runs{' '}
                <code>pg_restore</code> from the file, verifies the counts and the isolation, and activates. A live
                tenant is refused.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                restore.mutate({ slug: restoreForm.slug.trim(), name: restoreForm.name.trim(), file: restoreForm.file });
              }}
              className="space-y-4 p-6"
            >
              {restore.error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/30">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                  <pre className="whitespace-pre-wrap font-sans text-sm text-red-700 dark:text-red-300">{errorText(restore.error)}</pre>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Slug *</label>
                <input
                  type="text"
                  required
                  value={restoreForm.slug}
                  onChange={(e) => setRestoreForm({ ...restoreForm, slug: e.target.value.toLowerCase() })}
                  placeholder="acme"
                  className={input}
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  The same slug the dump was taken from. The file is <code>&lt;slug&gt;-&lt;date&gt;.dump</code>.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Display name</label>
                <input
                  type="text"
                  value={restoreForm.name}
                  onChange={(e) => setRestoreForm({ ...restoreForm, name: e.target.value })}
                  placeholder="Only needed when the registry row is gone too"
                  className={input}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Dump file *</label>
                <input
                  type="file"
                  required
                  accept=".dump,application/octet-stream"
                  onChange={(e) => setRestoreForm({ ...restoreForm, file: e.target.files?.[0] || null })}
                  className="block w-full text-sm text-neutral-700 dark:text-neutral-300"
                />
              </div>
              <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={() => setShowRestore(false)}
                  disabled={restore.isPending}
                  className="rounded-lg px-4 py-2 text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={restore.isPending || !restoreSlugOk || !restoreForm.file}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {restore.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {restore.isPending ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-neutral-800 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-neutral-200 p-6 dark:border-neutral-700">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">New tenant</h2>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Creates the schema, the database role, runs every migration and verifies isolation. Takes a few seconds.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                provision.mutate({ slug: form.slug.trim(), name: form.name.trim(), timezone: form.timezone.trim(), plan: form.plan });
              }}
              className="space-y-4 p-6"
            >
              {provision.error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/30">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                  <pre className="whitespace-pre-wrap font-sans text-sm text-red-700 dark:text-red-300">
                    {provision.error.response?.data?.error ||
                      provision.error.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join('\n') ||
                      (provision.error.code === 'ECONNABORTED'
                        ? 'The request timed out. The build continues on the server; submit again to resume it.'
                        : provision.error.message)}
                  </pre>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Slug *</label>
                <input
                  type="text"
                  required
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                  placeholder="acme"
                  className={input}
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  The subdomain: <code>{form.slug || 'acme'}.portal…</code> and <code>{form.slug || 'acme'}.api…</code>.
                  Lower-case letters, digits and hyphens. Cannot be changed later.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Display name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Acme Managed IT"
                  className={input}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Timezone *</label>
                <input
                  type="text"
                  required
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  placeholder="America/New_York"
                  className={input}
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  IANA name. Every date this MSP's customers see or are billed on is read in this zone; there is no
                  default on purpose.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Plan</label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={input}>
                  <option value="trial">Trial — everything for 14 days, then Base</option>
                  <option value="base">Base — the core portal; add-ons set on the tenant page</option>
                  <option value="legacy">Legacy — everything, with no end (pre-productisation installs only)</option>
                </select>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Change it, and grant add-ons, from the tenant's Plan panel at any time.
                </p>
              </div>
              <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  disabled={provision.isPending}
                  className="rounded-lg px-4 py-2 text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={provision.isPending || !slugOk || !form.name.trim() || !form.timezone.trim()}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {provision.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {provision.isPending ? 'Provisioning…' : 'Provision'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
